import { useMemo, useRef, useState } from 'react'
import { useProgramme, useCreateBlocks, useUpdateBlock, useDeleteBlock, useUpdateBlockGroup, useDeleteBlockGroup, useUpdateProgrammeYear, useImportProgramme } from '../../hooks/useProgramme'
import { Icon } from '../Icon'
import { formatEventDates } from '../../lib/dates'
import { readableTextOn, tintFrom } from '../../lib/programme-colours'
import { ManageCategoriesModal } from './ManageCategoriesModal'
import { ApiClient } from '../../api'
import type { ProgrammeBlock, ProgrammeCategory } from '../../types'

// Programming Overview — Calendar tab 1 (spec 41 + follow-ups). The editable
// annual planner: named blocks on days, coloured by an admin-editable palette.
// Categories act as pill filters (all on by default). Click a month to open a
// full single-month view. Year + month views each export a real PDF.

const WEEKDAY_HEAD = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

type BlockModalState =
  | { mode: 'add'; date?: string }
  | { mode: 'edit-single'; block: ProgrammeBlock }
  | { mode: 'edit-group'; title: string; categoryId: number }

export function ProgrammingOverviewTab({ year, isAdmin }: { year: number; isAdmin: boolean }) {
  const { data, isLoading, error } = useProgramme(year)

  const createBlocks = useCreateBlocks(year)
  const updateBlock = useUpdateBlock(year)
  const deleteBlock = useDeleteBlock(year)
  const updateGroup = useUpdateBlockGroup(year)
  const deleteGroup = useDeleteBlockGroup(year)
  const updateYear = useUpdateProgrammeYear(year)
  const importProgramme = useImportProgramme(year)

  const [openDayIso, setOpenDayIso] = useState<string | null>(null)
  const [blockModal, setBlockModal] = useState<BlockModalState | null>(null)
  const [showCategories, setShowCategories] = useState(false)
  const [editingSubtitle, setEditingSubtitle] = useState(false)
  const [subtitleDraft, setSubtitleDraft] = useState('')
  const [banner, setBanner] = useState<string | null>(null)
  const [hiddenCats, setHiddenCats] = useState<Set<number>>(new Set())
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const categories = data?.categories ?? []
  const catById = useMemo(() => {
    const m = new Map<number, ProgrammeCategory>()
    categories.forEach((c) => m.set(c.id, c))
    return m
  }, [categories])

  const isVisible = (id: number) => !hiddenCats.has(id)
  const visibleCatIds = useMemo(
    () => new Set(categories.filter((c) => isVisible(c.id)).map((c) => c.id)),
    [categories, hiddenCats]
  )

  // Visible blocks (respecting the category filter) grouped by day.
  const byDate = useMemo(() => {
    const m = new Map<string, ProgrammeBlock[]>()
    for (const b of data?.blocks ?? []) {
      if (hiddenCats.has(b.categoryId)) continue
      const arr = m.get(b.blockDate) ?? []
      arr.push(b)
      m.set(b.blockDate, arr)
    }
    for (const arr of m.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
    return m
  }, [data, hiddenCats])

  // Category counts (all blocks — stable regardless of filter). Pass a month
  // to scope the count to that month.
  function countFor(catId: number, month: number | null): number {
    const prefix = month == null ? `${year}-` : `${year}-${String(month + 1).padStart(2, '0')}`
    return (data?.blocks ?? []).filter((b) => b.categoryId === catId && b.blockDate.startsWith(prefix)).length
  }

  // Visible blocks grouped by title (for the summary lists). Optionally scoped
  // to a month.
  function groupsFor(month: number | null) {
    const g = new Map<string, { title: string; categoryId: number; dates: string[] }>()
    const prefix = month == null ? `${year}-` : `${year}-${String(month + 1).padStart(2, '0')}`
    for (const b of data?.blocks ?? []) {
      if (hiddenCats.has(b.categoryId)) continue
      if (!b.blockDate.startsWith(prefix)) continue
      const cur = g.get(b.title) ?? { title: b.title, categoryId: b.categoryId, dates: [] }
      cur.dates.push(b.blockDate)
      g.set(b.title, cur)
    }
    return Array.from(g.values()).sort((a, b) => (a.dates.slice().sort()[0] ?? '').localeCompare(b.dates.slice().sort()[0] ?? ''))
  }

  function toggleCat(id: number) {
    setHiddenCats((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function flash(msg: string) {
    setBanner(msg)
    window.setTimeout(() => setBanner(null), 4000)
  }

  async function handleExportJson() {
    try {
      const payload = await ApiClient.programme.export(year)
      downloadBlob(JSON.stringify(payload, null, 2), `programming-calendar-${year}.json`, 'application/json')
    } catch (e) {
      flash(`Export failed: ${(e as Error).message}`)
    }
  }

  async function handleImportFile(file: File) {
    try {
      const json = JSON.parse(await file.text())
      await importProgramme.mutateAsync(json)
      flash('Programme imported.')
    } catch (e) {
      flash(`Import failed: ${(e as Error).message}`)
    }
  }

  function pdfInput() {
    return { year, subtitle: data?.subtitle ?? null, categories, blocks: data?.blocks ?? [], visibleCatIds }
  }

  // jsPDF is heavy — load the PDF module on demand so it stays out of the main bundle.
  async function runPdf(kind: 'year' | 'month', month?: number) {
    try {
      const mod = await import('../../lib/programme-pdf')
      if (kind === 'year') mod.exportYearPdf(pdfInput())
      else mod.exportMonthPdf(pdfInput(), month!)
    } catch (e) {
      flash(`PDF failed: ${(e as Error).message}`)
    }
  }

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
        Could not load the programme: {(error as Error).message}
      </div>
    )
  }

  const subtitle = data?.subtitle ?? 'Programming overview'

  // Modals shared by both views.
  const modals = (
    <>
      {openDayIso && (
        <DayModal
          dateIso={openDayIso}
          blocks={byDate.get(openDayIso) ?? []}
          catById={catById}
          isAdmin={isAdmin}
          onClose={() => setOpenDayIso(null)}
          onAdd={() => { setBlockModal({ mode: 'add', date: openDayIso }); setOpenDayIso(null) }}
          onEdit={(b) => { setBlockModal({ mode: 'edit-single', block: b }); setOpenDayIso(null) }}
          onDelete={(id) => deleteBlock.mutate(id)}
        />
      )}
      {blockModal && (
        <BlockModal
          state={blockModal}
          year={year}
          categories={categories}
          onClose={() => setBlockModal(null)}
          onCreate={async (body) => { await createBlocks.mutateAsync(body); setBlockModal(null) }}
          onUpdateSingle={async (id, body) => { await updateBlock.mutateAsync({ id, body }); setBlockModal(null) }}
          onUpdateGroup={async (body) => { await updateGroup.mutateAsync(body); setBlockModal(null) }}
        />
      )}
      {showCategories && (
        <ManageCategoriesModal categories={categories} onClose={() => setShowCategories(false)} />
      )}
    </>
  )

  const onDayClick = (d: string) => {
    const has = (byDate.get(d)?.length ?? 0) > 0
    if (has) setOpenDayIso(openDayIso === d ? null : d)
    else if (isAdmin) setBlockModal({ mode: 'add', date: d })
  }

  // ---- Expanded single-month view ----
  if (expandedMonth != null) {
    const m = expandedMonth
    const monthGroups = groupsFor(m)
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setExpandedMonth(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green hover:text-green"
            >
              <Icon name="arrow-left" size={14} />Back to year
            </button>
            <h2 className="font-display text-2xl">{MONTH_NAMES[m]} {year}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setBlockModal({ mode: 'add', date: iso(year, m, 1) })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-green text-white hover:opacity-90"
              >
                <Icon name="plus" size={14} />Add block
              </button>
            )}
            <button
              type="button"
              onClick={() => runPdf('month', m)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green hover:text-green"
            >
              <Icon name="download" size={14} />PDF
            </button>
          </div>
        </div>

        <CategoryFilter categories={categories} count={(id) => countFor(id, m)} hidden={hiddenCats} onToggle={toggleCat} />

        <FullMonth year={year} month={m} byDate={byDate} catById={catById} isAdmin={isAdmin} onDayClick={onDayClick} />

        <SummaryList title={`Blocks in ${MONTH_NAMES[m]} ${year}`} groups={monthGroups} catById={catById} isAdmin={isAdmin}
          onEdit={(g) => setBlockModal({ mode: 'edit-group', title: g.title, categoryId: g.categoryId })}
          onDelete={(g) => { if (window.confirm(`Delete the whole “${g.title}” block (${g.dates.length} day(s))?`)) deleteGroup.mutate(g.title) }}
        />

        {modals}
      </div>
    )
  }

  // ---- Year overview ----
  return (
    <div className="space-y-5">
      {/* Subtitle + actions */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-[12rem]">
          {editingSubtitle && isAdmin ? (
            <input
              autoFocus
              value={subtitleDraft}
              onChange={(e) => setSubtitleDraft(e.target.value)}
              onBlur={() => { setEditingSubtitle(false); updateYear.mutate({ subtitle: subtitleDraft }) }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { setEditingSubtitle(false); updateYear.mutate({ subtitle: subtitleDraft }) }
                if (e.key === 'Escape') setEditingSubtitle(false)
              }}
              className="border border-line rounded px-2 py-1 text-sm bg-white focus:outline-none focus:border-green"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-ink-soft">{subtitle}</span>
              {isAdmin && (
                <button type="button" onClick={() => { setSubtitleDraft(subtitle); setEditingSubtitle(true) }} className="text-ink-soft/60 hover:text-green" aria-label="Edit subtitle">
                  <Icon name="edit" size={13} />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button type="button" onClick={() => setBlockModal({ mode: 'add' })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-green text-white hover:opacity-90">
              <Icon name="plus" size={14} />Add block
            </button>
          )}
          {isAdmin && (
            <button type="button" onClick={() => setShowCategories(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-green hover:border-green">
              <Icon name="settings" size={14} />Manage categories
            </button>
          )}
          <button type="button" onClick={() => runPdf('year')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green hover:text-green">
            <Icon name="download" size={14} />PDF
          </button>
          <button type="button" onClick={handleExportJson} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green hover:text-green">
            <Icon name="file" size={14} />Export JSON
          </button>
          {isAdmin && (
            <>
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green hover:text-green">
                <Icon name="upload" size={14} />Import
              </button>
              <input ref={fileRef} type="file" accept="application/json,.json" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); e.target.value = '' }} />
            </>
          )}
        </div>
      </div>

      {banner && <div className="bg-green-soft border border-green text-green rounded px-3 py-2 text-sm">{banner}</div>}

      {/* Category pill filters */}
      <CategoryFilter categories={categories} count={(id) => countFor(id, null)} hidden={hiddenCats} onToggle={toggleCat} />

      {/* 12-month grid */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {Array.from({ length: 12 }).map((_, m) => (
          <MiniMonth
            key={m}
            year={year}
            month={m}
            byDate={byDate}
            catById={catById}
            isAdmin={isAdmin}
            onDayClick={onDayClick}
            openDayIso={openDayIso}
            onExpand={() => setExpandedMonth(m)}
          />
        ))}
      </div>

      <SummaryList title={`Blocks in ${year}`} groups={groupsFor(null)} catById={catById} isAdmin={isAdmin}
        emptyHint={isAdmin ? ' Use “Add block” to start.' : ''}
        onEdit={(g) => setBlockModal({ mode: 'edit-group', title: g.title, categoryId: g.categoryId })}
        onDelete={(g) => { if (window.confirm(`Delete the whole “${g.title}” block (${g.dates.length} day(s))?`)) deleteGroup.mutate(g.title) }}
      />

      {modals}
    </div>
  )
}

// ---- Category filter pills -----------------------------------------------

function CategoryFilter({
  categories, count, hidden, onToggle
}: {
  categories: ProgrammeCategory[]
  count: (id: number) => number
  hidden: Set<number>
  onToggle: (id: number) => void
}) {
  if (categories.length === 0) {
    return <div className="bg-white border border-line rounded px-4 py-2.5 text-sm text-ink-soft">No categories yet.</div>
  }
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border border-line rounded px-4 py-2.5">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mr-1">Filter</span>
      {categories.map((c) => {
        const on = !hidden.has(c.id)
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onToggle(c.id)}
            aria-pressed={on}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors"
            style={on
              ? { background: tintFrom(c.colour), color: '#1a1a1a', borderColor: c.colour }
              : { background: 'transparent', color: '#9a9a9a', borderColor: '#e0dccf' }}
          >
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: on ? c.colour : '#c9c4b5' }} />
            {c.label}
            <span className={on ? 'text-ink-soft font-normal' : 'text-ink-soft/50 font-normal'}>{count(c.id)}</span>
            {!on && <Icon name="x" size={11} />}
          </button>
        )
      })}
    </div>
  )
}

// ---- Summary list --------------------------------------------------------

function SummaryList({
  title, groups, catById, isAdmin, emptyHint, onEdit, onDelete
}: {
  title: string
  groups: Array<{ title: string; categoryId: number; dates: string[] }>
  catById: Map<number, ProgrammeCategory>
  isAdmin: boolean
  emptyHint?: string
  onEdit: (g: { title: string; categoryId: number; dates: string[] }) => void
  onDelete: (g: { title: string; categoryId: number; dates: string[] }) => void
}) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-2.5 border-b border-line font-display text-base">{title}</div>
      {groups.length === 0 && (
        <div className="px-4 py-6 text-sm text-ink-soft text-center">No programme blocks here.{emptyHint ?? ''}</div>
      )}
      {groups.map((g) => {
        const cat = catById.get(g.categoryId)
        return (
          <div key={g.title} className="flex items-center gap-3 px-4 py-2 border-b border-line last:border-b-0 text-sm">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: cat?.colour ?? '#999' }} />
            <span className="font-semibold min-w-[9rem] truncate">{g.title}</span>
            <span className="text-ink-soft flex-1">
              {formatEventDates(g.dates)} <span className="text-ink-soft/60 text-xs">({g.dates.length} day{g.dates.length === 1 ? '' : 's'})</span>
            </span>
            {cat && <span className="text-xs text-ink-soft">{cat.label}</span>}
            {isAdmin && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => onEdit(g)} className="text-ink-soft/70 hover:text-green p-1" aria-label={`Edit ${g.title}`}><Icon name="edit" size={14} /></button>
                <button type="button" onClick={() => onDelete(g)} className="text-ink-soft/70 hover:text-oxblood p-1" aria-label={`Delete ${g.title}`}><Icon name="x" size={14} /></button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---- Mini month (year grid) ----------------------------------------------

function MiniMonth({
  year, month, byDate, catById, isAdmin, onDayClick, openDayIso, onExpand
}: {
  year: number
  month: number
  byDate: Map<string, ProgrammeBlock[]>
  catById: Map<number, ProgrammeCategory>
  isAdmin: boolean
  onDayClick: (iso: string) => void
  openDayIso: string | null
  onExpand: () => void
}) {
  const offset = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7
  const dayCount = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`
  const monthBlockCount = Array.from(byDate.keys()).filter((k) => k.startsWith(monthPrefix)).length

  return (
    <div className="bg-white border border-line rounded p-3">
      <button
        type="button"
        onClick={onExpand}
        className="w-full flex items-baseline justify-between mb-1.5 px-1 group"
        title={`Open ${MONTH_NAMES[month]} full view`}
      >
        <span className="font-display text-sm font-semibold group-hover:text-green flex items-center gap-1">
          {MONTH_NAMES[month]}
          <Icon name="chevronRight" size={12} className="opacity-0 group-hover:opacity-100 text-green" />
        </span>
        {monthBlockCount > 0 && <span className="text-[10px] text-ink-soft font-mono">{monthBlockCount}</span>}
      </button>
      <div className="grid grid-cols-7 text-center text-[9px] uppercase tracking-wide text-ink-soft mb-1">
        {WEEKDAY_HEAD.map((w, i) => <div key={i}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: offset }).map((_, i) => <div key={`pad-${i}`} />)}
        {Array.from({ length: dayCount }).map((_, i) => {
          const day = i + 1
          const d = iso(year, month, day)
          const blocks = byDate.get(d)
          const has = !!blocks && blocks.length > 0
          const primary = has ? catById.get(blocks![0]!.categoryId) : undefined
          const isOpen = openDayIso === d
          const clickable = has || isAdmin
          return (
            <button
              key={day}
              type="button"
              onClick={() => clickable && onDayClick(d)}
              title={has ? blocks!.map((b) => b.title).join(', ') : undefined}
              className={
                'aspect-square rounded text-[11px] flex items-center justify-center relative overflow-hidden ' +
                (clickable ? 'cursor-pointer ' : '') +
                (has ? 'font-semibold ' : 'text-ink-soft/60 ') +
                (!has && isAdmin ? 'hover:bg-parchment ' : '') +
                (isOpen ? 'ring-2 ring-green ' : '')
              }
              style={has && primary ? { background: primary.colour, color: readableTextOn(primary.colour) } : undefined}
            >
              <span className="relative z-10">{day}</span>
              {has && blocks!.length > 1 && (
                <span aria-hidden className="absolute top-0 right-0.5 text-[8px] font-mono font-bold leading-none" style={{ color: readableTextOn(primary!.colour) }}>
                  +{blocks!.length - 1}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---- Full month (expanded) -----------------------------------------------

function FullMonth({
  year, month, byDate, catById, isAdmin, onDayClick
}: {
  year: number
  month: number
  byDate: Map<string, ProgrammeBlock[]>
  catById: Map<number, ProgrammeCategory>
  isAdmin: boolean
  onDayClick: (iso: string) => void
}) {
  const offset = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7
  const dayCount = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const cells: (number | null)[] = [...Array(offset).fill(null), ...Array.from({ length: dayCount }, (_, i) => i + 1)]
  while (cells.length % 7 !== 0) cells.push(null)

  return (
    <div className="bg-white border border-line rounded p-4">
      <div className="grid grid-cols-7 text-center text-xs uppercase tracking-wide text-ink-soft mb-2 font-semibold">
        {WEEKDAY_HEAD.map((w, i) => <div key={i}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((day, i) => {
          if (day == null) return <div key={`pad-${i}`} className="min-h-[92px]" />
          const d = iso(year, month, day)
          const blocks = byDate.get(d)
          const has = !!blocks && blocks.length > 0
          const clickable = has || isAdmin
          return (
            <button
              key={d}
              type="button"
              onClick={() => clickable && onDayClick(d)}
              className={
                'min-h-[92px] rounded border text-left p-1.5 flex flex-col gap-1 align-top ' +
                (clickable ? 'cursor-pointer hover:border-green ' : 'cursor-default ') +
                'border-line bg-white'
              }
            >
              <span className="text-xs font-semibold text-ink-soft">{day}</span>
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {(blocks ?? []).slice(0, 4).map((b) => {
                  const cat = catById.get(b.categoryId)
                  const colour = cat?.colour ?? '#999'
                  return (
                    <span key={b.id} className="text-[10px] font-semibold rounded px-1 py-0.5 truncate" style={{ background: colour, color: readableTextOn(colour) }}>
                      {b.title}
                    </span>
                  )
                })}
                {blocks && blocks.length > 4 && <span className="text-[10px] text-ink-soft">+{blocks.length - 4} more</span>}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---- Day modal -----------------------------------------------------------

function DayModal({
  dateIso, blocks, catById, isAdmin, onClose, onAdd, onEdit, onDelete
}: {
  dateIso: string
  blocks: ProgrammeBlock[]
  catById: Map<number, ProgrammeCategory>
  isAdmin: boolean
  onClose: () => void
  onAdd: () => void
  onEdit: (b: ProgrammeBlock) => void
  onDelete: (id: number) => void
}) {
  const pretty = new Date(dateIso + 'T00:00:00Z').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
  })
  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg">{pretty}</div>
        <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink" aria-label="Close"><Icon name="x" size={18} /></button>
      </div>
      <div className="space-y-1.5">
        {blocks.length === 0 && <div className="text-sm text-ink-soft">Nothing on this day.</div>}
        {blocks.map((b) => {
          const cat = catById.get(b.categoryId)
          return (
            <div key={b.id} className="flex items-center gap-2 border border-line rounded px-2.5 py-1.5">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: cat?.colour ?? '#999' }} />
              <span className="font-semibold flex-1 truncate">{b.title}</span>
              {cat && <span className="text-xs text-ink-soft">{cat.label}</span>}
              {isAdmin && (
                <>
                  <button type="button" onClick={() => onEdit(b)} className="text-ink-soft/70 hover:text-green p-1" aria-label="Edit"><Icon name="edit" size={14} /></button>
                  <button type="button" onClick={() => onDelete(b.id)} className="text-ink-soft/70 hover:text-oxblood p-1" aria-label="Delete"><Icon name="x" size={14} /></button>
                </>
              )}
            </div>
          )
        })}
      </div>
      {isAdmin && (
        <button type="button" onClick={onAdd} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold border border-line text-green hover:border-green">
          <Icon name="plus" size={14} />Add a block on this day
        </button>
      )}
    </Overlay>
  )
}

// ---- Block modal (add / edit-single / edit-group) ------------------------

function BlockModal({
  state, year, categories, onClose, onCreate, onUpdateSingle, onUpdateGroup
}: {
  state: BlockModalState
  year: number
  categories: ProgrammeCategory[]
  onClose: () => void
  onCreate: (body: { title: string; categoryId: number; startDate: string; endDate: string }) => Promise<void>
  onUpdateSingle: (id: number, body: { title: string; categoryId: number; blockDate: string }) => Promise<void>
  onUpdateGroup: (body: { title: string; newTitle: string; categoryId: number }) => Promise<void>
}) {
  const firstCat = categories[0]?.id ?? 0
  const initialTitle = state.mode === 'edit-single' ? state.block.title : state.mode === 'edit-group' ? state.title : ''
  const initialCat = state.mode === 'edit-single' ? state.block.categoryId : state.mode === 'edit-group' ? state.categoryId : firstCat
  const initialStart = state.mode === 'edit-single' ? state.block.blockDate : state.mode === 'add' && state.date ? state.date : `${year}-01-01`

  const [title, setTitle] = useState(initialTitle)
  const [categoryId, setCategoryId] = useState<number>(initialCat)
  const [startDate, setStartDate] = useState(initialStart)
  const [endDate, setEndDate] = useState(initialStart)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const heading = state.mode === 'add' ? 'Add block' : state.mode === 'edit-single' ? 'Edit block' : `Edit “${state.title}”`
  const minD = `${year}-01-01`
  const maxD = `${year}-12-31`

  async function submit() {
    if (!title.trim()) { setErr('Title is required.'); return }
    if (!categoryId) { setErr('Pick a category.'); return }
    setBusy(true); setErr(null)
    try {
      if (state.mode === 'add') {
        await onCreate({ title: title.trim(), categoryId, startDate, endDate: endDate < startDate ? startDate : endDate })
      } else if (state.mode === 'edit-single') {
        await onUpdateSingle(state.block.id, { title: title.trim(), categoryId, blockDate: startDate })
      } else {
        await onUpdateGroup({ title: state.title, newTitle: title.trim(), categoryId })
      }
    } catch (e) {
      setErr((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg">{heading}</div>
        <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink" aria-label="Close"><Icon name="x" size={18} /></button>
      </div>
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Title</span>
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Santa Express"
            className="mt-1 w-full border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Category</span>
          <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))}
            className="mt-1 w-full border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>

        {state.mode === 'add' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">From</span>
              <input type="date" min={minD} max={maxD} value={startDate}
                onChange={(e) => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value) }}
                className="mt-1 w-full border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">To</span>
              <input type="date" min={startDate} max={maxD} value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green" />
            </label>
          </div>
        )}

        {state.mode === 'edit-single' && (
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Date</span>
            <input type="date" min={minD} max={maxD} value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full border border-line rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-green" />
          </label>
        )}

        {state.mode === 'edit-group' && (
          <p className="text-xs text-ink-soft">Renaming / recolouring applies to every day of this block in {year}.</p>
        )}

        {err && <div className="text-sm text-oxblood">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:border-green">Cancel</button>
        <button type="button" disabled={busy} onClick={submit} className="px-3 py-1.5 rounded text-sm font-semibold bg-green text-white hover:opacity-90 disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Overlay>
  )
}

// ---- Shared overlay ------------------------------------------------------

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mt-16 p-5" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

// ---- Helpers -------------------------------------------------------------

function downloadBlob(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
